import { Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { FieldValueLines } from "./FieldValueLines.tsx"
import { GroupedComparisonCell } from "./GroupedComparisonCell.tsx"
import { MilestoneChangesPanel } from "./MilestoneChangesPanel.tsx"
import type { ComparisonGroupedValue } from "./comparison-grouped-value.types.ts"

const { Text } = Typography

/** The two field keys whose cells render through `GroupedComparisonCell`
 * instead of the plain bulleted `FieldValueLines` — a milestone list and a
 * payment schedule are both "a list of named, priced items" grouped by
 * name, where a flowing bullet list stops being usable once either grows
 * past a handful of entries. Every other field key keeps the simple path,
 * unconditionally — this table never hardcodes a field list otherwise. */
const GROUPED_FIELD_KEYS = new Set(["scope", "payment_terms"])

/** Wide enough for the longest field label this registry produces
 * ("Secondary Phone", "Project Description") to sit on one line. The
 * column used to be a percentage, which let a tall Scope cell squeeze it
 * until `break-words` wrapped "Project Type" one letter per line. */
const FIELD_COLUMN_WIDTH = 190

/** Below this the table scrolls sideways inside its own box rather than
 * compressing columns past readability — `FIELD_COLUMN_WIDTH` plus a
 * usable minimum for three source columns. */
const MIN_TABLE_WIDTH = FIELD_COLUMN_WIDTH + 3 * 180

interface ComparisonTableColumn {
  /** Stable key, matched against each row's `values`/`cellNotes` map — not
   * necessarily "lead"/"proposal"/"contract"; the Contract Generate modal
   * uses whichever of "lead"/"proposal" is the row's actual baseline. */
  key: string
  label: string
  /** Second line under the label — the document's version and status, e.g.
   * "V2 · Approved". Rendered muted and smaller, so the column still reads
   * as "Proposal" at a glance. */
  sublabel?: string
}

interface ComparisonTableRow {
  key: string
  label: string
  /**
   * Whether this row differs at all. Drives the row's `data-diff` marker
   * and, on its own, the differing-value emphasis for every column —
   * `diffColumns` narrows that to the cells that actually differ.
   */
  hasDiff: boolean
  /**
   * The column keys whose values really differ from the baseline. Omit it
   * (the two Generate modals do — they compare exactly two columns, so a
   * row that differs at all differs in the one non-baseline cell) and a
   * `hasDiff` row emphasizes every column, as it always has.
   */
  diffColumns?: string[]
  /** Pre-resolved display string per column key — already defaulted to
   * whatever "not set" placeholder the caller uses; this component never
   * invents a fallback. A missing entry renders as an empty cell. */
  values: Partial<Record<string, string>>
  /** Small muted note appended after the Field label, e.g. "(Lead only)"
   * for a field that can't be synced back into a document. */
  note?: string
  /** Small muted note rendered under one column's cell, keyed by column
   * key — e.g. "Locked (signed)" under a locked Contract value. */
  cellNotes?: Partial<Record<string, string>>
  /**
   * Structured per-column data for `scope`/`payment_terms` rows — parallel
   * to `values`, and purely additive: a column present here renders via
   * `GroupedComparisonCell` instead of the plain bulleted fallback; a
   * column absent here keeps rendering through `values`/`FieldValueLines`.
   * Ignored entirely for every row key outside `GROUPED_FIELD_KEYS`.
   */
  groupedValues?: Partial<Record<string, ComparisonGroupedValue>>
  /** Already-translated copy `GroupedComparisonCell` needs and cannot
   * derive itself — only meaningful (and required) on a `scope`/
   * `payment_terms` row that also sets `groupedValues`. */
  groupedCellCopy?: {
    identicalMessage: string
    itemNounSingular: string
    itemNounPlural: string
    groupNounSingular: string
    groupNounPlural: string
  }
}

interface ComparisonTableProps {
  /** 2 to 4 source columns — the Field column is always added in front. */
  columns: ComparisonTableColumn[]
  /**
   * One row per canonical field the caller wants shown — render generically
   * off however many are present; never hardcode a field list here or at
   * a call site, so a newly added backend field (e.g. `sales_price`) shows
   * up with no client change.
   */
  rows: ComparisonTableRow[]
  /** Prefixes every `data-testid` this component renders:
   * `${testIdPrefix}-table`, `${testIdPrefix}-row-<key>`,
   * `${testIdPrefix}-cell-<key>-<columnKey>`. */
  testIdPrefix: string
  fieldColumnLabel?: string
  /**
   * Makes the table its own scroll box, with the header row pinned to the
   * top of it, instead of growing and letting an ancestor scroll. Pass it
   * when the table is the only thing that should scroll in a modal whose
   * header, question and footer all have to stay put.
   */
  scrollable?: boolean
  /**
   * Where a grouped cell's "View changes" opens. "inline" (default) lists
   * the changes inside the cell. "row" opens them in a full-width row under
   * the field, titled by `changesTitle` — for a three-column table, where a
   * one-third-width cell is too narrow to read a before/after comparison.
   */
  changesLayout?: "inline" | "row"
  /** Already-translated title for a "row" changes panel, given the column
   * whose changes it shows. */
  changesTitle?: (column: ComparisonTableColumn) => string
}

/**
 * The Lead/Proposal/Contract field-comparison table shared by the three
 * Generate/Confirm-time modals (Project creation, Proposal Generate,
 * Contract Generate). A real HTML table — light header, a thin bottom
 * border per row rather than a heavy grid, generous top-aligned padding so
 * a multi-line Scope/Payment Terms cell doesn't force a short neighboring
 * cell to center awkwardly, and natural wrapping (never truncation) so a
 * long value stays fully readable. No per-row "Recommended: ..." caption —
 * callers that have a recommendation concept (only the Project modal does)
 * surface it once, outside this table, as a single decision.
 *
 * Columns are laid out `fixed`: the Field column is a hard pixel width and
 * the source columns split the rest evenly, so the widest cell in a column
 * can no longer steal room from its neighbours.
 */
function ComparisonTable({
  columns,
  rows,
  testIdPrefix,
  fieldColumnLabel,
  scrollable,
  changesLayout = "inline",
  changesTitle
}: ComparisonTableProps) {
  const { t } = useTranslation()
  const [openChanges, setOpenChanges] = useState<Set<string>>(() => new Set())
  const changesKey = (rowKey: string, columnKey: string) =>
    `${rowKey}::${columnKey}`
  const toggleChanges = (key: string) =>
    setOpenChanges((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const resolvedFieldColumnLabel =
    fieldColumnLabel ?? t("documentSync.fieldColumn")

  const cellDiffers = (row: ComparisonTableRow, columnKey: string) => {
    if (!row.hasDiff) return false
    if (!row.diffColumns) return true
    return row.diffColumns.includes(columnKey)
  }

  return (
    <div
      className={
        "border border-gray-200 rounded-lg bg-white" +
        (scrollable ? " overflow-auto flex-1 min-h-0" : " overflow-x-auto")
      }
    >
      <table
        className="w-full text-sm border-collapse table-fixed"
        style={{ minWidth: MIN_TABLE_WIDTH }}
        data-testid={`${testIdPrefix}-table`}
      >
        <colgroup>
          <col style={{ width: FIELD_COLUMN_WIDTH }} />
          {columns.map((column) => (
            <col key={column.key} />
          ))}
        </colgroup>
        <thead className={scrollable ? "sticky top-0 z-10" : undefined}>
          <tr className="text-left">
            <th className="bg-gray-50 border-b border-gray-200 py-3 px-4 font-semibold text-gray-700 align-bottom">
              {resolvedFieldColumnLabel}
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                className="bg-gray-50 border-b border-gray-200 py-3 px-4 font-semibold text-gray-700 align-bottom"
                data-testid={`${testIdPrefix}-header-${column.key}`}
              >
                <div>{column.label}</div>
                {column.sublabel && (
                  <Text type="secondary" className="text-xs font-normal block">
                    {column.sublabel}
                  </Text>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={row.key}>
              <tr
                className="border-b border-gray-200 last:border-b-0 align-top"
                data-testid={`${testIdPrefix}-row-${row.key}`}
                data-diff={row.hasDiff ? "true" : "false"}
              >
                <td className="py-3 px-4 align-top">
                  <Text strong className="leading-snug">
                    {row.label}
                  </Text>
                  {row.note && (
                    <Text type="secondary" className="text-xs">
                      {" "}
                      {row.note}
                    </Text>
                  )}
                </td>
                {columns.map((column) => {
                  const value = row.values[column.key] ?? ""
                  const cellNote = row.cellNotes?.[column.key]
                  const grouped =
                    GROUPED_FIELD_KEYS.has(row.key) &&
                    row.groupedValues?.[column.key]
                  const differs = cellDiffers(row, column.key)

                  return (
                    <td
                      key={column.key}
                      data-testid={`${testIdPrefix}-cell-${row.key}-${column.key}`}
                      data-diff={differs ? "true" : "false"}
                      className={
                        "py-3 px-4 align-top break-words" +
                        (differs
                          ? " bg-amber-50 border-l-2 border-amber-300"
                          : "")
                      }
                    >
                      {grouped && row.groupedCellCopy ? (
                        <GroupedComparisonCell
                          groups={grouped.groups}
                          diff={grouped.diff}
                          identicalMessage={
                            row.groupedCellCopy.identicalMessage
                          }
                          itemNounSingular={
                            row.groupedCellCopy.itemNounSingular
                          }
                          itemNounPlural={row.groupedCellCopy.itemNounPlural}
                          groupNounSingular={
                            row.groupedCellCopy.groupNounSingular
                          }
                          groupNounPlural={row.groupedCellCopy.groupNounPlural}
                          testIdPrefix={`${testIdPrefix}-cell-${row.key}-${column.key}`}
                          {...(changesLayout === "row"
                            ? {
                                onToggleChanges: () =>
                                  toggleChanges(
                                    changesKey(row.key, column.key)
                                  ),
                                changesExpanded: openChanges.has(
                                  changesKey(row.key, column.key)
                                )
                              }
                            : {})}
                        />
                      ) : (
                        <FieldValueLines
                          value={value}
                          fieldKey={row.key}
                          variant="bulleted"
                        />
                      )}
                      {cellNote && (
                        <Text type="secondary" className="text-xs block mt-1">
                          {cellNote}
                        </Text>
                      )}
                    </td>
                  )
                })}
              </tr>
              {changesLayout === "row" &&
                columns.map((column) => {
                  const diff = row.groupedValues?.[column.key]?.diff
                  if (
                    !diff?.length ||
                    !openChanges.has(changesKey(row.key, column.key))
                  ) {
                    return null
                  }
                  return (
                    <tr
                      key={`${row.key}-${column.key}-changes`}
                      className="border-b border-gray-200"
                      data-testid={`${testIdPrefix}-changes-row-${row.key}-${column.key}`}
                    >
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 pb-4 pt-1"
                      >
                        <MilestoneChangesPanel
                          entries={diff}
                          title={
                            changesTitle ? changesTitle(column) : column.label
                          }
                          testIdPrefix={`${testIdPrefix}-cell-${row.key}-${column.key}`}
                        />
                      </td>
                    </tr>
                  )
                })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export { ComparisonTable, type ComparisonTableColumn, type ComparisonTableRow }
